import { useState, useEffect } from 'react'
import { 
  Search, 
  FileText, 
  Settings, 
  Cpu, 
  BookOpen, 
  MessageSquare, 
  Layers,
  ExternalLink,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Server,
  Globe,
  Database
} from 'lucide-react'

interface Endpoint {
  method: string
  path: string
  description: string
  auth: boolean
}

interface EndpointCategory {
  category: string
  endpoints: Endpoint[]
}

interface GotchaLayer {
  title: string
  description: string
  files?: { name: string; description: string }[]
  categories?: { name: string; description: string }[]
}

interface SystemData {
  framework: string
  version: string
  description: string
  layers: Record<string, GotchaLayer>
  endpoints: { category: string; count: number }[]
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-700 border-green-200',
  POST: 'bg-blue-100 text-blue-700 border-blue-200',
  PATCH: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  PUT: 'bg-orange-100 text-orange-700 border-orange-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
}

const LAYER_ICONS: Record<string, React.ReactNode> = {
  goals: <BookOpen className="w-5 h-5" />,
  tools: <Cpu className="w-5 h-5" />,
  context: <FileText className="w-5 h-5" />,
  hardprompts: <MessageSquare className="w-5 h-5" />,
  args: <Settings className="w-5 h-5" />,
}

function SystemDashboard() {
  const [systemData, setSystemData] = useState<SystemData | null>(null)
  const [endpoints, setEndpoints] = useState<EndpointCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['endpoints', 'gotcha']))
  const [healthStatus, setHealthStatus] = useState<{ status: string; latency: number } | null>(null)

  // In production (Cloudflare Pages), API is at the same origin or configured via env
  // In development, proxy handles /api routing via vite.config.ts
  const API_URL = import.meta.env.PROD 
    ? (import.meta.env.VITE_API_URL || '')  // Production: same origin or env URL
    : ''  // Development: empty string uses vite proxy

  useEffect(() => {
    fetchSystemData()
    checkHealth()
  }, [])

  const fetchSystemData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [systemRes, endpointsRes] = await Promise.all([
        fetch(`${API_URL}/api/system`),
        fetch(`${API_URL}/api/system/endpoints`),
      ])

      if (!systemRes.ok || !endpointsRes.ok) {
        throw new Error('Failed to fetch system data')
      }

      const systemJson = await systemRes.json()
      const endpointsJson = await endpointsRes.json()

      if (systemJson.success) {
        setSystemData(systemJson.data)
      }
      if (endpointsJson.success) {
        setEndpoints(endpointsJson.data)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const checkHealth = async () => {
    const start = performance.now()
    try {
      const res = await fetch(`${API_URL}/api/health`)
      const json = await res.json()
      const latency = Math.round(performance.now() - start)
      setHealthStatus({ 
        status: json.success ? json.data.status : 'unhealthy',
        latency 
      })
    } catch {
      setHealthStatus({ status: 'offline', latency: 0 })
    }
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }

  const filterEndpoints = (categories: EndpointCategory[]) => {
    if (!searchQuery) return categories
    const query = searchQuery.toLowerCase()
    return categories.map(cat => ({
      ...cat,
      endpoints: cat.endpoints.filter(e =>
        e.path.toLowerCase().includes(query) ||
        e.description.toLowerCase().includes(query) ||
        cat.category.toLowerCase().includes(query)
      ),
    })).filter(cat => cat.endpoints.length > 0)
  }

  const filterLayers = (layers: Record<string, GotchaLayer>) => {
    if (!searchQuery) return layers
    const query = searchQuery.toLowerCase()
    const filtered: Record<string, GotchaLayer> = {}
    
    Object.entries(layers).forEach(([key, layer]) => {
      const matchesLayer = layer.title.toLowerCase().includes(query) ||
                          layer.description.toLowerCase().includes(query)
      
      const matchingFiles = layer.files?.filter(f =>
        f.name.toLowerCase().includes(query) ||
        f.description.toLowerCase().includes(query)
      ) || []

      const matchingCategories = layer.categories?.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query)
      ) || []

      if (matchesLayer || matchingFiles.length > 0 || matchingCategories.length > 0) {
        filtered[key] = {
          ...layer,
          files: matchingFiles.length > 0 ? matchingFiles : layer.files,
          categories: matchingCategories.length > 0 ? matchingCategories : layer.categories,
        }
      }
    })
    
    return filtered
  }

  const filteredEndpoints = filterEndpoints(endpoints)
  const filteredLayers = systemData ? filterLayers(systemData.layers) : {}

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-600">Loading system dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchSystemData}
            className="btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">System Dashboard</h1>
                  <p className="text-sm text-gray-500">
                    {systemData?.framework} Framework v{systemData?.version}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Health Status */}
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                healthStatus?.status === 'healthy' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                {healthStatus?.status === 'healthy' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <div className="text-sm">
                  <span className="font-medium text-gray-900">
                    {healthStatus?.status === 'healthy' ? 'Online' : 'Offline'}
                  </span>
                  {healthStatus && healthStatus.latency > 0 && (
                    <span className="text-gray-500 ml-2">({healthStatus.latency}ms)</span>
                  )}
                </div>
              </div>
              <button 
                onClick={checkHealth}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh health check"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatCard 
              icon={<Server className="w-5 h-5 text-blue-600" />}
              label="API Endpoints"
              value={endpoints.reduce((acc, cat) => acc + cat.endpoints.length, 0)}
            />
            <StatCard 
              icon={<BookOpen className="w-5 h-5 text-purple-600" />}
              label="Goals"
              value={systemData?.layers.goals.files?.length || 0}
            />
            <StatCard 
              icon={<Cpu className="w-5 h-5 text-orange-600" />}
              label="Tool Categories"
              value={systemData?.layers.tools.categories?.length || 0}
            />
            <StatCard 
              icon={<Database className="w-5 h-5 text-green-600" />}
              label="Framework Layers"
              value={Object.keys(systemData?.layers || {}).length}
            />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search endpoints, goals, tools, context..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* API Endpoints */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleSection('endpoints')}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-gray-900">API Endpoints</h2>
                  <p className="text-sm text-gray-500">Available REST API routes</p>
                </div>
              </div>
              {expandedSections.has('endpoints') ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {expandedSections.has('endpoints') && (
              <div className="border-t border-gray-200">
                {filteredEndpoints.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No endpoints match your search
                  </div>
                ) : (
                  filteredEndpoints.map((category) => (
                    <div key={category.category} className="border-b border-gray-100 last:border-b-0">
                      <div className="px-6 py-3 bg-gray-50">
                        <h3 className="text-sm font-semibold text-gray-700">{category.category}</h3>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {category.endpoints.map((endpoint, idx) => (
                          <div 
                            key={idx}
                            className="px-6 py-4 hover:bg-gray-50 transition-colors group"
                          >
                            <div className="flex items-start gap-3">
                              <span className={`text-xs font-mono font-semibold px-2 py-1 rounded border ${
                                METHOD_COLORS[endpoint.method] || 'bg-gray-100 text-gray-700'
                              }`}>
                                {endpoint.method}
                              </span>
                              <div className="flex-1 min-w-0">
                                <code className="text-sm font-mono text-gray-900 block truncate">
                                  {endpoint.path}
                                </code>
                                <p className="text-sm text-gray-500 mt-1">{endpoint.description}</p>
                              </div>
                              {endpoint.auth && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                                  Auth
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* GOTCHA Framework */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleSection('gotcha')}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-gray-900">GOTCHA Framework</h2>
                  <p className="text-sm text-gray-500">System architecture layers</p>
                </div>
              </div>
              {expandedSections.has('gotcha') ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {expandedSections.has('gotcha') && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {Object.entries(filteredLayers).length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No framework layers match your search
                  </div>
                ) : (
                  Object.entries(filteredLayers).map(([key, layer]) => (
                    <div key={key} className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-gray-600">{LAYER_ICONS[key]}</div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{layer.title}</h3>
                          <p className="text-sm text-gray-500">{layer.description}</p>
                        </div>
                      </div>
                      
                      {layer.files && layer.files.length > 0 && (
                        <div className="ml-8 space-y-2">
                          {layer.files.map((file, idx) => (
                            <div 
                              key={idx}
                              className="flex items-start gap-2 text-sm group"
                            >
                              <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <code className="text-gray-700 font-mono text-xs">{file.name}</code>
                                <p className="text-gray-500 text-xs">{file.description}</p>
                              </div>
                              <a 
                                href={`/${key}/${file.name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="opacity-0 group-hover:opacity-100 text-primary-600 hover:text-primary-700 transition-opacity"
                                title="View file"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {layer.categories && layer.categories.length > 0 && (
                        <div className="ml-8 grid grid-cols-2 gap-2">
                          {layer.categories.map((cat, idx) => (
                            <div 
                              key={idx}
                              className="bg-gray-50 rounded-lg p-3 text-sm"
                            >
                              <code className="text-gray-700 font-mono text-xs block">{cat.name}/</code>
                              <p className="text-gray-500 text-xs mt-1">{cat.description}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Documentation Links */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <DocLinkCard
            href="/goals/manifest.md"
            title="Goals Documentation"
            description="Process definitions and workflows"
            icon={<BookOpen className="w-5 h-5" />}
          />
          <DocLinkCard
            href="/tools/manifest.md"
            title="Tools Reference"
            description="Available tools and scripts"
            icon={<Cpu className="w-5 h-5" />}
          />
          <DocLinkCard
            href="/ARCHITECTURE.md"
            title="Architecture Guide"
            description="Full system architecture"
            icon={<Layers className="w-5 h-5" />}
          />
        </div>
      </div>
    </div>
  )
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center">
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  )
}

function DocLinkCard({ 
  href, 
  title, 
  description, 
  icon 
}: { 
  href: string; 
  title: string; 
  description: string; 
  icon: React.ReactNode 
}) {
  return (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
    >
      <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center text-primary-600 group-hover:bg-primary-100 transition-colors">
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
    </a>
  )
}

export default SystemDashboard
